import { useState, useEffect, useCallback } from 'react'
import { supabase } from '../lib/supabase'
import { setUserProperties, setUserGroup } from '../lib/analytics'

export type PlanTier = 'free' | 'basic' | 'growth' | 'trial'

interface CreditsInfo {
  creditsUsed: number
  monthlyLimit: number
  bonusCredits: number
  /** Trial users: trial_email_limit. Others: monthlyLimit + bonusCredits. */
  effectiveLimit: number
  creditsRemaining: number
  planTier: PlanTier
  /** Trial-only fields (null/undefined when not on trial) */
  trialEmailLimit: number
  trialEndsAt: string | null
  /** Which paid plan the trial converts to on day 15 ('basic' | 'growth' | 'free').
      Null when user is not on trial. Lets the UI mark that plan as "your trial
      converts here" instead of showing it as an upgrade target. */
  trialTargetPlan: 'basic' | 'growth' | 'free' | null
  /** ISO timestamp Stripe will end the subscription at, when user has cancelled.
      Null = active subscription, no cancellation pending. Set by stripe-webhook
      when cancel_at_period_end becomes true; cleared when undone. */
  cancellationScheduledAt: string | null
  /** Admin override: grants warmup + placement access regardless of plan */
  deliverabilityUnlocked: boolean
  /** ISO timestamp of when credits renew (from Stripe billing period) */
  currentPeriodEnd: string | null
  isLoading: boolean
}

/** Refresh credits every 30 seconds + on window focus */
const POLL_INTERVAL_MS = 30_000

export function useCredits(userId: string | undefined): CreditsInfo {
  const [creditsUsed, setCreditsUsed] = useState(0)
  const [monthlyLimit, setMonthlyLimit] = useState(20)
  const [bonusCredits, setBonusCredits] = useState(0)
  const [planTier, setPlanTier] = useState<PlanTier>('free')
  const [trialEmailLimit, setTrialEmailLimit] = useState(200)
  const [trialEndsAt, setTrialEndsAt] = useState<string | null>(null)
  const [trialTargetPlan, setTrialTargetPlan] = useState<'basic' | 'growth' | 'free' | null>(null)
  const [cancellationScheduledAt, setCancellationScheduledAt] = useState<string | null>(null)
  const [deliverabilityUnlocked, setDeliverabilityUnlocked] = useState(false)
  const [currentPeriodEnd, setCurrentPeriodEnd] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(true)

  const fetchCredits = useCallback(async (showLoading = false) => {
    if (!userId) return
    if (showLoading) setIsLoading(true)
    try {
      const [profileRes, creditsRes] = await Promise.all([
        supabase.from('profiles').select('monthly_limit, plan_tier, bonus_credits, current_period_end, deliverability_unlocked, trial_email_limit, trial_ends_at, cancellation_scheduled_at, trial_target_plan').eq('id', userId).single(),
        supabase.rpc('get_credits_used', { p_user_id: userId }),
      ])

      if (profileRes.data?.monthly_limit != null) {
        setMonthlyLimit(profileRes.data.monthly_limit)
      }

      setBonusCredits(profileRes.data?.bonus_credits ?? 0)
      setDeliverabilityUnlocked(profileRes.data?.deliverability_unlocked ?? false)
      setCurrentPeriodEnd(profileRes.data?.current_period_end ?? null)
      setTrialEmailLimit(profileRes.data?.trial_email_limit ?? 200)
      setTrialEndsAt(profileRes.data?.trial_ends_at ?? null)
      setCancellationScheduledAt((profileRes.data as { cancellation_scheduled_at?: string | null })?.cancellation_scheduled_at ?? null)
      setTrialTargetPlan((profileRes.data as { trial_target_plan?: 'basic' | 'growth' | 'free' | null })?.trial_target_plan ?? null)

      if (profileRes.data?.plan_tier) {
        const tier = profileRes.data.plan_tier as PlanTier
        setPlanTier(tier)
        setUserProperties({ plan_tier: tier, monthly_limit: profileRes.data.monthly_limit })
        setUserGroup('plan', tier)
      }

      if (creditsRes.data != null) {
        setCreditsUsed(creditsRes.data as number)
      }
    } catch (e) {
      console.error('[useCredits] error:', e)
    } finally {
      if (showLoading) setIsLoading(false)
    }
  }, [userId])

  // Initial fetch
  useEffect(() => {
    fetchCredits(true)
  }, [fetchCredits])

  // Poll every 30s
  useEffect(() => {
    if (!userId) return
    const id = setInterval(() => fetchCredits(false), POLL_INTERVAL_MS)
    return () => clearInterval(id)
  }, [userId, fetchCredits])

  // Refetch on window focus (user switches tab and comes back)
  useEffect(() => {
    if (!userId) return
    const handleFocus = () => fetchCredits(false)
    window.addEventListener('focus', handleFocus)
    return () => window.removeEventListener('focus', handleFocus)
  }, [userId, fetchCredits])

  // Trial users have a fixed total cap (trial_email_limit), not a monthly limit.
  // For everyone else, effectiveLimit = monthlyLimit + bonusCredits.
  const effectiveLimit = planTier === 'trial' ? trialEmailLimit : monthlyLimit + bonusCredits

  return {
    creditsUsed,
    monthlyLimit,
    bonusCredits,
    effectiveLimit,
    creditsRemaining: Math.max(0, effectiveLimit - creditsUsed),
    planTier,
    trialEmailLimit,
    trialEndsAt,
    trialTargetPlan,
    cancellationScheduledAt,
    deliverabilityUnlocked,
    currentPeriodEnd,
    isLoading,
  }
}
